<template>
  <div class="entries-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-history"></i>
        Histórico de Descarga
      </h1>
      <p class="page-subtitle">Visualize o histórico de descarga baseado nas predições de apostas</p>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-filter"></i>
          Filtros de Consulta
        </h3>
      </div>
      
      <div class="filters-content">
        <div class="filters-grid">
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-map-location-dot"></i>
              Regiões *
            </label>
            <div class="multiselect-container">
              <MultiSelect 
                v-model="selectedRegions" 
                :options="availableRegions" 
                placeholder="Selecione as origens da descarga"
                :showSelectAllButtons="true"
              />
            </div>
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-trophy"></i>
              Loteria
            </label>
            <select v-model="selectedLottery" class="form-input">
              <option value="">Todas as loterias</option>
              <option 
                v-for="lottery in baseLotteries" 
                :key="lottery.id" 
                :value="lottery.id"
              >
                {{ lottery.name }}
              </option>
            </select>
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-clock"></i>
              Horário da Loteria
            </label>
            <select v-model="selectedLotteryTime" class="form-input" :disabled="!selectedLottery">
              <option value="">Todos os horários</option>
              <option 
                v-for="timeOption in availableTimes" 
                :key="timeOption.id" 
                :value="timeOption.id"
              >
                {{ timeOption.name }}
              </option>
            </select>
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-hashtag"></i>
              Número
            </label>
            <input 
              class="form-input" 
              type="text" 
              v-model="selectedNumber"
              placeholder="Ex: 123, 1234 (busca exata)"
              maxlength="4"
              @input="validateNumberInput"
            />
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-calendar-days"></i>
              Data Inicial *
            </label>
            <input 
              class="form-input" 
              type="date" 
              v-model="startDate"
              placeholder="Data inicial"
            />
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-calendar-days"></i>
              Data Final *
            </label>
            <input 
              class="form-input" 
              type="date" 
              v-model="endDate"
              placeholder="Data final"
            />
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-info-circle"></i>
              Status
            </label>
            <select v-model="selectedStatus" class="form-input">
              <option value="">Todos os status</option>
              <option value="PENDING">Pendente</option>
              <option value="PROCESSED">Processado</option>
              <option value="AWARDED">Premiado</option>
            </select>
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-sitemap"></i>
              Hierarquia
            </label>
            <select 
              v-model="selectedHierarchy" 
              class="form-input"
              :disabled="shouldDisableHierarchyFilter"
              :title="hierarchyFilterTooltip"
            >
              <option value="">Todas as hierarquias</option>
              <option value="Normal → Descarga">Normal → Descarga</option>
              <option value="Descarga → Redescarga">Descarga → Redescarga</option>
            </select>
          </div>

        </div>

        <div class="filters-actions">
          <button 
            type="button" 
            class="btn-primary" 
            @click="searchPredictions()"
            :disabled="loading"
          >
            <i v-if="loading" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-search"></i>
            {{ loading ? 'Consultando...' : 'Pesquisar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando dados...</span>
      </div>
    </div>

    <!-- Resumo Consolidado -->
    <div v-show="!loading && searchExecuted && filteredPredictions && filteredPredictions.length > 0" class="results-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-chart-bar"></i>
          Resumo Consolidado
        </h3>
      </div>
      
      <div class="summary-content">
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-value">{{ filteredPredictions.length }}</div>
            <div class="summary-label">Total de Registros</div>
          </div>
          <div class="summary-item">
            <div class="summary-value currency-positive">{{ setCurrencyString(totalBetValue) }}</div>
            <div class="summary-label">Total Apostado</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabela de Predições -->
    <div v-show="!loading && searchExecuted && filteredPredictions && filteredPredictions.length > 0" class="results-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-table"></i>
          Histórico de Descarga
        </h3>
        <div class="card-actions">
          <div class="table-zoom-controls" title="Zoom da tabela">
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom <= 5"
              @click="decreaseTableZoom"
              title="Diminuir zoom"
            >
              <i class="fa-solid fa-magnifying-glass-minus"></i>
            </button>
            <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
              {{ tableZoom }}%
            </span>
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom >= 100"
              @click="increaseTableZoom"
              title="Aumentar zoom"
            >
              <i class="fa-solid fa-magnifying-glass-plus"></i>
            </button>
          </div>
          <div class="columns-dropdown" ref="columnsDropdown">
            <button
              type="button"
              class="btn-action btn-columns"
              @click.stop="showColumnsMenu = !showColumnsMenu"
              title="Mostrar ou esconder colunas"
            >
              <i class="fa-solid fa-columns"></i>
              Colunas
              <i class="fa-solid fa-caret-down columns-caret"></i>
            </button>
            <div v-if="showColumnsMenu" class="columns-menu" @click.stop>
              <div class="columns-menu-header">
                <span>Colunas visíveis</span>
                <button type="button" class="columns-reset" @click="resetVisibleColumns">
                  Restaurar
                </button>
              </div>
              <label
                v-for="col in toggleableColumns"
                :key="col.key"
                class="columns-menu-item"
                :class="{ locked: col.locked }"
              >
                <input
                  type="checkbox"
                  :checked="isColumnVisible(col.key)"
                  :disabled="col.locked"
                  @change="toggleColumn(col.key)"
                />
                <span>{{ col.label }}</span>
              </label>
            </div>
          </div>
          <button 
            type="button" 
            class="btn-action btn-print" 
            @click="printReport()"
            title="Imprimir relatório"
          >
            <i class="fa-solid fa-print"></i>
            Imprimir
          </button>
          <button 
            type="button" 
            class="btn-action btn-pdf" 
            @click="exportToPDF()"
            title="Exportar para PDF"
          >
            <i class="fa-solid fa-file-pdf"></i>
            PDF
          </button>
          <button 
            type="button" 
            class="btn-action btn-excel" 
            @click="exportToExcel()"
            title="Exportar para Excel"
          >
            <i class="fa-solid fa-file-excel"></i>
            Excel
          </button>
        </div>
      </div>
      
      <div class="table-container" :style="tableZoomStyle">
        <table class="modern-table">
          <thead>
            <tr v-if="!isLimitedUser">
              <th v-if="isColumnVisible('hierarchy')">Hierarquia</th>
              <th v-if="isColumnVisible('lottery')">Loteria e Horário</th>
              <th v-if="isColumnVisible('modality')">Modalidade</th>
              <th v-if="isColumnVisible('number')">Número</th>
              <th v-if="isColumnVisible('place')">Colocação</th>
              <th v-if="isColumnVisible('betValue')">Jogo Reg.</th>
              <th v-if="isColumnVisible('currentPrice')">Cotação</th>
              <th v-if="isColumnVisible('valueAward')">Prêmio</th>
              <th v-if="isColumnVisible('limit')">Limite</th>
              <th v-if="isColumnVisible('sobra')">Sobra</th>
              <th v-if="isColumnVisible('bancaPay')" title="Cotação da próxima descarga que vai receber">Banca Paga</th>
              <th v-if="isColumnVisible('value')">Valor</th>
              <th v-if="isColumnVisible('status')">Status</th>
              <th v-if="isColumnVisible('awardValue')">Valor Premiado</th>
            </tr>
            <tr v-else>
              <th v-if="isColumnVisible('hierarchy')">Hierarquia</th>
              <th v-if="isColumnVisible('lottery')">Loteria e Horário</th>
              <th v-if="isColumnVisible('modality')">Modalidade</th>
              <th v-if="isColumnVisible('number')">Número</th>
              <th v-if="isColumnVisible('place')">Colocação</th>
              <th v-if="isColumnVisible('value')">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="history in paginatedPredictions" :key="`${history.originRegionId}_${history.destinationRegionId}_${history.lotteryDetailId}_${history.number}_${history.place}_${history.hierarchyLevel}_${history.currentPrice}_${history.descargaBetId}_${history.betValue}`" class="data-row">
              <!-- Visualização Completa (Usuário Normal) -->
              <template v-if="!isLimitedUser">
                <td v-if="isColumnVisible('hierarchy')">
                  <span 
                    class="hierarchy-badge" 
                    :class="getHierarchyClass(history.hierarchyLevel)"
                    :title="getHierarchyTooltip(history.hierarchyLevel)"
                  >
                    {{ history.originRegionName }} → {{ history.destinationRegionName }}
                  </span>
                </td>
                <td v-if="isColumnVisible('lottery')">
                  <div class="lottery-info">
                    <span class="lottery-name">{{ getLotteryDisplayName(history) }}</span>
                  </div>
                </td>
                <td v-if="isColumnVisible('modality')">{{ history.modalityName }}</td>
                <td v-if="isColumnVisible('number')" class="number-cell">{{ history.number }}</td>
                <td v-if="isColumnVisible('place')" class="place-cell">{{ history.place }}</td>
                <td v-if="isColumnVisible('betValue')">
                  <span 
                    class="currency-value clickable-value" 
                    @click="openBetDetailsModal(history)"
                    :title="'Clique para ver detalhes das apostas'"
                  >
                    {{ setCurrencyString(history.betValue || 0) }}
                  </span>
                </td>
                <td v-if="isColumnVisible('currentPrice')">
                  <span class="currency-value">{{ history.currentPrice ? history.currentPrice.toFixed(0) : '-' }}</span>
                </td>
                <td v-if="isColumnVisible('valueAward')">
                  <span class="currency-value currency-positive">{{ setCurrencyString(history.valueAward || 0) }}</span>
                </td>
                <td v-if="isColumnVisible('limit')">
                  <span class="currency-value">{{ setCurrencyString(history.limit) }}</span>
                </td>
                <td v-if="isColumnVisible('sobra')">
                  <span :class="['currency-value', { 'currency-positive': history.sobra > 0, 'currency-negative': history.sobra < 0 }]">
                    {{ setCurrencyString(history.sobra) }}
                  </span>
                </td>
                <td v-if="isColumnVisible('bancaPay')">
                  <span class="currency-value">{{ history.bancaPay ? history.bancaPay.toFixed(0) : '-' }}</span>
                </td>
                <td v-if="isColumnVisible('value')">
                  <span :class="['currency-value', { 'currency-positive': history.value > 0, 'currency-negative': history.value < 0 }]">
                    {{ setCurrencyString(history.value || 0) }}
                  </span>
                </td>
                <td v-if="isColumnVisible('status')">
                  <span class="status-badge" :class="getStatusClass(history.status, history.betWin)">
                    {{ getStatusLabel(history.status, history.betWin) }}
                  </span>
                </td>
                <td v-if="isColumnVisible('awardValue')">
                  <span v-if="history.betWin && history.awardValue > 0" class="currency-value currency-positive">
                    {{ setCurrencyString(history.sobra) }}
                  </span>
                  <span v-else class="currency-value">
                    -
                  </span>
                </td>
              </template>

              <!-- Visualização Limitada (Usuário Limitado) - APENAS 6 COLUNAS -->
              <template v-else>
                <td v-if="isColumnVisible('hierarchy')">
                  <span 
                    class="hierarchy-badge" 
                    :class="getHierarchyClass(history.hierarchyLevel)"
                    :title="getHierarchyTooltip(history.hierarchyLevel)"
                  >
                    {{ history.originRegionName }} → {{ history.destinationRegionName }}
                  </span>
                </td>
                <td v-if="isColumnVisible('lottery')">
                  <div class="lottery-info">
                    <span class="lottery-name">{{ getLotteryDisplayName(history) }}</span>
                  </div>
                </td>
                <td v-if="isColumnVisible('modality')">{{ history.modalityName }}</td>
                <td v-if="isColumnVisible('number')" class="number-cell">{{ history.number }}</td>
                <td v-if="isColumnVisible('place')" class="place-cell">{{ history.place }}</td>
                <td v-if="isColumnVisible('value')">
                  <span :class="['currency-value', { 'currency-positive': history.value > 0, 'currency-negative': history.value < 0 }]">
                    {{ setCurrencyString(history.value || 0) }}
                  </span>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginação -->
      <div v-if="totalPages > 1" class="pagination-container">
        <div class="pagination-info">
          Mostrando {{ ((currentPage - 1) * itemsPerPage) + 1 }} a {{ Math.min(currentPage * itemsPerPage, filteredPredictions.length) }} de {{ filteredPredictions.length }} registros
        </div>
        <div class="pagination-controls">
          <button 
            class="pagination-btn" 
            @click="currentPage = 1"
            :disabled="currentPage === 1"
          >
            <i class="fa-solid fa-angle-double-left"></i>
          </button>
          <button 
            class="pagination-btn" 
            @click="currentPage--"
            :disabled="currentPage === 1"
          >
            <i class="fa-solid fa-angle-left"></i>
          </button>
          <span class="pagination-page-info">
            Página {{ currentPage }} de {{ totalPages }}
          </span>
          <button 
            class="pagination-btn" 
            @click="currentPage++"
            :disabled="currentPage >= totalPages"
          >
            <i class="fa-solid fa-angle-right"></i>
          </button>
          <button 
            class="pagination-btn" 
            @click="currentPage = totalPages"
            :disabled="currentPage >= totalPages"
          >
            <i class="fa-solid fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Estado vazio -->
    <div v-show="!loading && searchExecuted && (!filteredPredictions || !filteredPredictions.length)" class="empty-state">
      <div class="empty-state-content">
        <i class="fa-solid fa-history"></i>
        <h3>Nenhum dado encontrado</h3>
        <p>Selecione pelo menos uma origem da descarga e as datas inicial e final para consultar o histórico de descarga. Os campos "Loteria", "Horário" e "Número" são filtros opcionais. Aqui você verá o histórico completo da hierarquia: Normal → Descarga → Redescarga.</p>
      </div>
    </div>

    <!-- Modal de Detalhes das Apostas -->
    <div v-if="showBetDetailsModal" class="modal-overlay" @click="closeBetDetailsModal">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fa-solid fa-list-alt"></i>
            Detalhes das Apostas - Jogo Reg
          </h3>
          <button class="modal-close-btn" @click="closeBetDetailsModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-content">
          <div class="bet-details-info">
            <div class="info-row">
              <span class="info-label">Hierarquia:</span>
              <span class="info-value">{{ selectedBetDetails?.originRegionName }} → {{ selectedBetDetails?.destinationRegionName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Loteria:</span>
              <span class="info-value">{{ getLotteryDisplayName(selectedBetDetails) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Modalidade:</span>
              <span class="info-value">{{ selectedBetDetails?.modalityName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Número:</span>
              <span class="info-value">{{ selectedBetDetails?.number }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Colocação:</span>
              <span class="info-value">{{ selectedBetDetails?.place }}</span>
            </div>
          </div>

          <div class="bet-details-summary">
            <div class="summary-card">
              <div class="summary-label">Total Jogo Reg (BetValue)</div>
              <div class="summary-value currency-positive">{{ setCurrencyString(selectedBetDetails?.betValue || 0) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Cotação Média</div>
              <div class="summary-value">{{ selectedBetDetails?.currentPrice ? selectedBetDetails.currentPrice.toFixed(0) : '-' }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Prêmio Total (ValueAward)</div>
              <div class="summary-value currency-positive">{{ setCurrencyString(selectedBetDetails?.valueAward || 0) }}</div>
            </div>
          </div>

          <div class="bet-details-explanation">
            <div class="explanation-section">
              <h4><i class="fa-solid fa-info-circle"></i> Como o Jogo Reg é calculado?</h4>
              <p>
                O <strong>Jogo Reg (betValue)</strong> representa o valor total apostado pelas apostas originais 
                que compõem esta descarga para este número e colocação específicos.
              </p>
              <ul>
                <li><strong>BetValue:</strong> Soma dos valores apostados de todas as apostas que contribuíram para esta descarga</li>
                <li><strong>CurrentPrice (Cotação):</strong> Média ponderada das cotações das apostas originais</li>
                <li><strong>ValueAward (Prêmio):</strong> BetValue × CurrentPrice = Prêmio potencial total</li>
                <li><strong>Sobra:</strong> ValueAward - Limite da região = Valor que excedeu o limite</li>
                <li><strong>Valor (para descarga):</strong> Sobra ÷ Cotação da próxima descarga</li>
              </ul>
            </div>

            <div class="explanation-section">
              <h4><i class="fa-solid fa-calculator"></i> Exemplo de Cálculo</h4>
              <div class="example-box">
                <p><strong>Cenário:</strong></p>
                <ul>
                  <li>3 apostas no número "1234", colocação 1º</li>
                  <li>Aposta 1: R$ 10,00 × 600 = R$ 6.000,00</li>
                  <li>Aposta 2: R$ 15,00 × 600 = R$ 9.000,00</li>
                  <li>Aposta 3: R$ 5,00 × 600 = R$ 3.000,00</li>
                  <li><strong>Total BetValue:</strong> R$ 30,00</li>
                  <li><strong>Total ValueAward:</strong> R$ 18.000,00</li>
                  <li><strong>Limite da região:</strong> R$ 5.000,00</li>
                  <li><strong>Sobra:</strong> R$ 18.000,00 - R$ 5.000,00 = R$ 13.000,00</li>
                  <li><strong>Cotação próxima descarga:</strong> 500</li>
                  <li><strong>Valor para descarga:</strong> R$ 13.000,00 ÷ 500 = R$ 26,00</li>
                </ul>
              </div>
            </div>

            <div class="explanation-section warning-section">
              <h4><i class="fa-solid fa-exclamation-triangle"></i> Importante</h4>
              <p>
                O sistema agrupa todas as apostas do mesmo número e colocação automaticamente. 
                Quando múltiplas apostas são feitas, elas são somadas para calcular o total que vai para a descarga.
              </p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="closeBetDetailsModal">
            <i class="fa-solid fa-times"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";
import MultiSelect from "@/components/utils/MultiSelect.vue";
import { ROLES } from "@/services/permissions";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const HISTORICO_DESCARGA_COLUMNS_STORAGE_KEY = "historicoDescarga.visibleColumns";
const TABLE_ZOOM_STORAGE_KEY = "ab.resultsTableZoom";
const TABLE_ZOOM_MIN = 5;
const TABLE_ZOOM_MAX = 100;
const TABLE_ZOOM_STEP = 5;

const DEFAULT_VISIBLE_COLUMNS = {
  hierarchy: true,
  lottery: true,
  modality: true,
  number: true,
  place: true,
  betValue: true,
  currentPrice: true,
  valueAward: true,
  limit: true,
  sobra: true,
  bancaPay: true,
  value: true,
  status: true,
  awardValue: true,
};

export default {
  name: "ViewHistoricoDescarga",
  components: { MultiSelect },
  data() {
    return {
      loading: true,
      searchExecuted: false,
      regions: [],
      lotteries: [],
      baseLotteries: [],
      predictions: [],
      selectedRegions: [],
      selectedLottery: "",
      selectedLotteryTime: "",
      startDate: "",
      endDate: "",
      selectedNumber: "",
      selectedStatus: "",
      selectedHierarchy: "",

      // Modal de detalhes das apostas
      showBetDetailsModal: false,
      selectedBetDetails: null,

      // Paginação
      currentPage: 1,
      itemsPerPage: 20,

      // Controle de usuário e permissões
      currentUser: null,
      userResponsibleRegions: [],
      userBancaId: null,
      bancas: [],

      // Controle de colunas da tabela
      showColumnsMenu: false,
      visibleColumns: { ...DEFAULT_VISIBLE_COLUMNS },
      tableZoom: 100,
    };
  },
  computed: {
    tableZoomStyle() {
      return { zoom: this.tableZoom / 100 };
    },

    currentSelectedRegion() {
      const savedRegion = localStorage.getItem('currentRegion');
      return savedRegion ? JSON.parse(savedRegion) : null;
    },

    currentSelectedBanca() {
      const savedBanca = localStorage.getItem('selectedBanca');
      return savedBanca ? JSON.parse(savedBanca) : null;
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    isLimitedUser() {
      return this.currentUser && this.currentUser.isLimitedUser === true;
    },

    availableRegions() {
      let regions = this.regions;
      
      if (this.isAdmin) {
        // ADMIN: aplicar filtro por banca selecionada primeiro se houver
        if (this.currentSelectedBanca) {
          regions = regions.filter(region => 
            String(region.bancaId) === String(this.currentSelectedBanca.id)
          );
        }
        
        // Para ADMIN, aplicar filtro por região ativa selecionada se houver
        if (this.currentSelectedRegion) {
          const selectedRegionId = String(this.currentSelectedRegion.id);
          const selectedRegion = this.regions.find(r => String(r.id) === selectedRegionId);
          
          // Aplicar regra: Se região Normal (OnlyDescarga=false), NÃO mostrar regiões de descarga (OnlyDescarga=true)
          if (selectedRegion && !selectedRegion.onlyDescarga) {
            // Região Normal: mostrar apenas ela mesma
            regions = regions.filter(region => String(region.id) === selectedRegionId);
          } else if (selectedRegion && selectedRegion.onlyDescarga && selectedRegion.descargaRegionId) {
            // Região Descarga: mostrar ela + destino + quem envia para ela
            regions = regions.filter(region => {
              const regionId = String(region.id);
              
              // A própria região
              if (regionId === selectedRegionId) return true;
              
              // Região de destino da descarga
              if (String(selectedRegion.descargaRegionId) === regionId) return true;
              
              // Regiões que enviam para a região selecionada
              if (region.descargaRegionId && String(region.descargaRegionId) === selectedRegionId) return true;
              
              return false;
            });
          } else if (selectedRegion && selectedRegion.onlyDescarga && !selectedRegion.descargaRegionId) {
            // Região Redescarga: mostrar apenas ela mesma (igual Normal)
            regions = regions.filter(region => String(region.id) === selectedRegionId);
          }
        }
        
        return regions.map(region => ({
            ...region,
            label: region.name,
            value: region.id
        }));
      } else if (this.isBanca) {
        // BANCA: filtrar por bancaId para pegar regiões que pertencem à banca
        if (this.userBancaId) {
          const bancaRegions = regions.filter(region => 
            String(region.bancaId) === String(this.userBancaId)
          );
          
          // Se há região selecionada, aplicar filtros específicos por tipo
          if (this.currentSelectedRegion) {
            const selectedRegionId = String(this.currentSelectedRegion.id);
            const selectedRegion = bancaRegions.find(r => String(r.id) === selectedRegionId);
            
            if (selectedRegion) {
              // Região Normal: mostrar apenas ela mesma
              if (!selectedRegion.onlyDescarga) {
                regions = bancaRegions.filter(region => String(region.id) === selectedRegionId);
              }
              // Região Descarga: mostrar histórico dela e de quem ela recebe (pode ser de outra banca)
              else if (selectedRegion.onlyDescarga && selectedRegion.descargaRegionId) {
                regions = regions.filter(region => {
                  const regionId = String(region.id);
                  
                  // A própria região (da banca)
                  if (regionId === selectedRegionId) return true;
                  
                  // Regiões que enviam para ela (PODEM SER DE OUTRAS BANCAS)
                  if (region.descargaRegionId && String(region.descargaRegionId) === selectedRegionId) return true;
                  
                  return false;
                });
              }
              // Região Redescarga: mostrar apenas ela mesma (igual Normal)
              else if (selectedRegion.onlyDescarga && !selectedRegion.descargaRegionId) {
                regions = bancaRegions.filter(region => String(region.id) === selectedRegionId);
              }
            } else {
              // Se a região selecionada não pertence à banca, mostrar todas da banca
              regions = bancaRegions;
            }
          } else {
            // Se não há região selecionada, mostrar todas da banca
            regions = bancaRegions;
          }
        } else {
          regions = [];
        }
        
        return regions.map(region => ({
          ...region,
          label: region.name,
          value: region.id
        }));
      } else {
        // Para GERENTE/COBRADOR: aplicar filtro por região ativa selecionada
        if (this.currentSelectedRegion) {
          const selectedRegionId = String(this.currentSelectedRegion.id);
          const selectedRegion = this.regions.find(r => String(r.id) === selectedRegionId);
          
          // Aplicar regra: Se região Normal (OnlyDescarga=false), NÃO mostrar regiões de descarga (OnlyDescarga=true)
          if (selectedRegion && !selectedRegion.onlyDescarga) {
            // Região Normal: mostrar apenas ela mesma
            regions = regions.filter(region => 
              String(region.id) === selectedRegionId &&
              this.userResponsibleRegions.includes(region.id)
            );
          } else if (selectedRegion && selectedRegion.onlyDescarga && selectedRegion.descargaRegionId) {
            // Região Descarga: mostrar histórico dela e de quem ela recebe
          regions = regions.filter(region => {
            const regionId = String(region.id);
            
              // A própria região
            if (regionId === selectedRegionId) return true;
            
              // Regiões que enviam para ela
              if (region.descargaRegionId && String(region.descargaRegionId) === selectedRegionId) {
                return this.userResponsibleRegions.includes(region.id);
              }
              
              // Região para onde ela envia
              if (String(selectedRegion.descargaRegionId) === regionId) {
                return this.userResponsibleRegions.includes(region.id);
            }
            
            return false;
          });
          } else if (selectedRegion && selectedRegion.onlyDescarga && !selectedRegion.descargaRegionId) {
            // Região Redescarga: mostrar apenas ela mesma (igual Normal)
            regions = regions.filter(region => 
              String(region.id) === selectedRegionId &&
              this.userResponsibleRegions.includes(region.id)
            );
          }
        } else {
          // Se não há região selecionada, aplicar filtro de permissões apenas
        regions = regions.filter(region => 
            this.userResponsibleRegions.includes(region.id)
        );
        }
        
        return regions.map(region => ({
            ...region,
            label: region.name,
            value: region.id
        }));
      }
    },

    availableTimes() {
      if (!this.selectedLottery) {
        return [];
      }
      
      // Encontrar a loteria selecionada e retornar seus detalhes (horários)
      const selectedLotteryData = this.baseLotteries.find(lottery => {
        // Comparação mais robusta para diferentes tipos de ID
        return lottery.id === this.selectedLottery || 
               String(lottery.id) === String(this.selectedLottery);
      });
      
      if (selectedLotteryData && selectedLotteryData.details && selectedLotteryData.details.length > 0) {
        return selectedLotteryData.details.map(detail => ({
          id: detail.id,
          name: detail.name
        }));
      }
      
      return [];
    },

    // Computed para obter o ID correto da loteria para busca
    selectedLotteryDetail() {
      if (this.selectedLotteryTime) {
        // Se um horário específico foi selecionado, usar esse ID
        return this.selectedLotteryTime;
      } else if (this.selectedLottery && this.availableTimes.length > 0) {
        // Se apenas a loteria foi selecionada, usar o primeiro horário disponível
        // (na verdade, vamos buscar todos os horários dessa loteria)
        return null; // Será tratado na função de busca
      }
      return null;
    },

    totalBetValue() {
      return this.predictions.reduce((sum, p) => sum + (p.value || 0), 0);
    },

    // Paginação
    paginatedPredictions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredPredictions.slice(start, end);
    },

    // Mapa lotteryDetailId -> hora (0-23) para ordenação - horário mais tarde primeiro
    lotteryDetailHourMap() {
      const map = {};
      (this.baseLotteries || []).forEach(lottery => {
        (lottery.details || []).forEach(detail => {
          const desc = detail.name || detail.description || '';
          const matchH = desc.match(/(\d{1,2})h/i);
          const matchColon = desc.match(/(\d{1,2}):/);
          const match = matchH || matchColon || desc.match(/(\d{1,2})/);
          const hour = match ? Math.min(23, Math.max(0, parseInt(match[1], 10))) : 0;
          map[detail.id] = hour;
        });
      });
      return map;
    },

    filteredPredictions() {
      let filtered = [...this.predictions];
      
      // Filtrar por status se selecionado
      if (this.selectedStatus) {
        if (this.selectedStatus === 'AWARDED') {
          // "Premiado" = apenas bilhetes que foram realmente premiados (betWin === true)
          filtered = filtered.filter(p => p.betWin === true);
        } else {
          filtered = filtered.filter(p => p.status === this.selectedStatus);
        }
      }
      
      // Filtrar por hierarquia baseado no tipo de região selecionada
      // Se apenas uma região está selecionada E não é ADMIN, aplicar filtro automático APENAS para regiões Normal ou Redescarga
      if (this.selectedRegions.length === 1 && !this.isAdmin) {
        const selectedRegion = this.selectedRegions[0];
        const regionData = this.regions.find(r => r.id === selectedRegion.id || String(r.id) === String(selectedRegion.id));
        
        if (regionData) {
          // Região Normal (onlyDescarga = false): só mostrar "Normal → Descarga"
          if (!regionData.onlyDescarga) {
            filtered = filtered.filter(p => p.hierarchyLevel === 'Normal → Descarga');
          }
          // Região Redescarga (onlyDescarga = true e não tem descargaRegionId ou é destino): só mostrar "Descarga → Redescarga"
          else if (regionData.onlyDescarga && (!regionData.descargaRegionId || this.isRedescargaRegion(regionData))) {
            filtered = filtered.filter(p => p.hierarchyLevel === 'Descarga → Redescarga');
          }
          // Região de Descarga (onlyDescarga = true e tem descargaRegionId): permitir escolher hierarquia manualmente
          // Se tem hierarquia selecionada manualmente, usar ela, senão mostrar todas
          else if (this.selectedHierarchy) {
            filtered = filtered.filter(p => p.hierarchyLevel === this.selectedHierarchy);
          }
          // Se não tem hierarquia selecionada, mostrar todas (Normal → Descarga e Descarga → Redescarga)
        }
      }
      // Se múltiplas regiões, é ADMIN, ou hierarquia selecionada manualmente, usar o filtro manual
      else if (this.selectedHierarchy) {
        filtered = filtered.filter(p => p.hierarchyLevel === this.selectedHierarchy);
      }
      
      // Ordenar: 1) Horário mais recente da loteria primeiro (18h > 16h > 14h)
      //          2) Dentro do mesmo horário, maior Prêmio (valueAward) primeiro
      const hourMap = this.lotteryDetailHourMap;
      const parseHourFromDisplay = (h) => {
        const id = h.lotteryDetailId || h.LotteryDetailId || '';
        if (hourMap[id] !== undefined) return hourMap[id];
        const display = h.lotteryDetailName || (h.lotteryName && h.lotteryDetailDescription ? `${h.lotteryName} ${h.lotteryDetailDescription}` : '');
        const m = (display || '').match(/(\d{1,2})h/i) || (display || '').match(/(\d{1,2}):/);
        return m ? Math.min(23, Math.max(0, parseInt(m[1], 10))) : 0;
      };
      filtered.sort((a, b) => {
        const hourA = parseHourFromDisplay(a);
        const hourB = parseHourFromDisplay(b);
        if (hourB !== hourA) return hourB - hourA;
        const premioA = Number(a.valueAward ?? a.ValueAward ?? 0);
        const premioB = Number(b.valueAward ?? b.ValueAward ?? 0);
        return premioB - premioA;
      });
      
      return filtered;
    },

    totalPages() {
      return Math.ceil(this.filteredPredictions.length / this.itemsPerPage);
    },

    // Verificar se deve desabilitar o filtro de hierarquia
    shouldDisableHierarchyFilter() {
      // Desabilitar apenas se não for ADMIN, apenas uma região está selecionada E a região é Normal ou Redescarga
      // Regiões de Descarga devem permitir escolher hierarquia
      if (!this.isAdmin && this.selectedRegions.length === 1) {
        const selectedRegion = this.selectedRegions[0];
        const regionData = this.regions.find(r => r.id === selectedRegion.id || String(r.id) === String(selectedRegion.id));
        
        if (regionData) {
          // Região Normal ou Redescarga: desabilitar (filtro automático)
          if (!regionData.onlyDescarga || (!regionData.descargaRegionId || this.isRedescargaRegion(regionData))) {
            return true;
          }
          // Região de Descarga: habilitar (permitir escolher)
          return false;
        }
      }
      // Para múltiplas regiões ou ADMIN: sempre habilitar
      return false;
    },

    // Tooltip para explicar o filtro automático
    hierarchyFilterTooltip() {
      // Só mostrar tooltip se não for ADMIN e apenas uma região está selecionada
      if (!this.isAdmin && this.selectedRegions.length === 1) {
        const selectedRegion = this.selectedRegions[0];
        const regionData = this.regions.find(r => r.id === selectedRegion.id || String(r.id) === String(selectedRegion.id));
        
        if (regionData) {
          if (!regionData.onlyDescarga) {
            return 'Filtro automático: Região Normal mostra apenas "Normal → Descarga"';
          } else if (regionData.onlyDescarga && (!regionData.descargaRegionId || this.isRedescargaRegion(regionData))) {
            return 'Filtro automático: Região Redescarga mostra apenas "Descarga → Redescarga"';
          }
          // Região de Descarga: não mostrar tooltip pois permite escolher hierarquia
        }
      }
      return '';
    },

    toggleableColumns() {
      if (this.isLimitedUser) {
        return [
          { key: "hierarchy", label: "Hierarquia", locked: true },
          { key: "lottery", label: "Loteria e Horário" },
          { key: "modality", label: "Modalidade" },
          { key: "number", label: "Número" },
          { key: "place", label: "Colocação" },
          { key: "value", label: "Valor" },
        ];
      }

      return [
        { key: "hierarchy", label: "Hierarquia", locked: true },
        { key: "lottery", label: "Loteria e Horário" },
        { key: "modality", label: "Modalidade" },
        { key: "number", label: "Número" },
        { key: "place", label: "Colocação" },
        { key: "betValue", label: "Jogo Reg." },
        { key: "currentPrice", label: "Cotação" },
        { key: "valueAward", label: "Prêmio" },
        { key: "limit", label: "Limite" },
        { key: "sobra", label: "Sobra" },
        { key: "bancaPay", label: "Banca Paga" },
        { key: "value", label: "Valor" },
        { key: "status", label: "Status" },
        { key: "awardValue", label: "Valor Premiado" },
      ];
    },
  },
  async mounted() {
    this.loadVisibleColumns();
    this.loadTableZoom();
    document.addEventListener("click", this.handleColumnsMenuOutsideClick);
    this.loading = true;
    try {
      await this.loadCurrentUser();
      
      // Inicializar filtros (datas como hoje)
      this.initializeFilters();
      
      // Carregar loterias primeiro
      await this.loadLotteries();
      
      // Depois carregar regiões (que vai configurar selectedRegions automaticamente)
      await this.searchRegions();
      
      // Carregar dados automaticamente após tudo estar pronto
      if (this.selectedRegions.length > 0 && this.startDate && this.endDate) {
        await this.searchPredictions();
      } else {
        this.loading = false;
      }
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleColumnsMenuOutsideClick);
  },
  watch: {
    currentSelectedRegion: {
      handler(newRegion, oldRegion) {
        // Quando a região ativa mudar, recarregar as regiões disponíveis
        this.updateSelectedRegions();
      },
      deep: true
    },

    currentSelectedBanca: {
      handler(newBanca, oldBanca) {
        // Quando a banca ativa mudar, recarregar as regiões disponíveis
        this.updateSelectedRegions();
      },
      deep: true
    },

    selectedLottery(newLottery, oldLottery) {
      // Quando a loteria mudar, resetar o horário selecionado
      if (newLottery !== oldLottery) {
        this.selectedLotteryTime = "";
      }
    },

    selectedRegions: {
      handler(newRegions, oldRegions) {
        // Quando as regiões selecionadas mudarem, ajustar o filtro de hierarquia automaticamente
        // Apenas se não for ADMIN e apenas uma região está selecionada
        if (!this.isAdmin && newRegions && newRegions.length === 1) {
          const selectedRegion = newRegions[0];
          const regionData = this.regions.find(r => r.id === selectedRegion.id || String(r.id) === String(selectedRegion.id));
          
          if (regionData) {
            // Região Normal: ajustar para "Normal → Descarga"
            if (!regionData.onlyDescarga) {
              this.selectedHierarchy = "Normal → Descarga";
            }
            // Região Redescarga: ajustar para "Descarga → Redescarga"
            else if (regionData.onlyDescarga && (!regionData.descargaRegionId || this.isRedescargaRegion(regionData))) {
              this.selectedHierarchy = "Descarga → Redescarga";
            }
            // Região de Descarga: manter o filtro selecionado (não limpar)
            // Não fazer nada, permitir que o usuário escolha
          }
        } else if (!this.isAdmin && newRegions && newRegions.length === 0) {
          // Se não há regiões selecionadas, limpar o filtro de hierarquia
          this.selectedHierarchy = "";
        } else if (this.isAdmin || (newRegions && newRegions.length > 1)) {
          // Se é ADMIN ou múltiplas regiões, manter o filtro selecionado (não limpar automaticamente)
          // Não fazer nada
        }
      },
      deep: true
    }
  },
  methods: {
    loadVisibleColumns() {
      try {
        const saved = localStorage.getItem(HISTORICO_DESCARGA_COLUMNS_STORAGE_KEY);
        if (!saved) {
          this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
          return;
        }
        const parsed = JSON.parse(saved);
        this.visibleColumns = {
          ...DEFAULT_VISIBLE_COLUMNS,
          ...(parsed && typeof parsed === "object" ? parsed : {}),
          hierarchy: true,
        };
      } catch {
        this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      }
    },

    saveVisibleColumns() {
      localStorage.setItem(
        HISTORICO_DESCARGA_COLUMNS_STORAGE_KEY,
        JSON.stringify(this.visibleColumns),
      );
    },

    loadTableZoom() {
      try {
        const saved = parseInt(localStorage.getItem(TABLE_ZOOM_STORAGE_KEY), 10);
        if (!Number.isNaN(saved) && saved >= TABLE_ZOOM_MIN && saved <= TABLE_ZOOM_MAX) {
          this.tableZoom = saved;
          return;
        }
      } catch {
        // ignore
      }
      this.tableZoom = 100;
    },

    saveTableZoom() {
      localStorage.setItem(TABLE_ZOOM_STORAGE_KEY, String(this.tableZoom));
    },

    decreaseTableZoom() {
      if (this.tableZoom <= TABLE_ZOOM_MIN) return;
      this.tableZoom = Math.max(TABLE_ZOOM_MIN, this.tableZoom - TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    increaseTableZoom() {
      if (this.tableZoom >= TABLE_ZOOM_MAX) return;
      this.tableZoom = Math.min(TABLE_ZOOM_MAX, this.tableZoom + TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    resetTableZoom() {
      this.tableZoom = 100;
      this.saveTableZoom();
    },

    isColumnVisible(key) {
      if (key === "hierarchy") {
        return true;
      }
      return this.visibleColumns[key] !== false;
    },

    toggleColumn(key) {
      if (key === "hierarchy") {
        return;
      }
      this.visibleColumns = {
        ...this.visibleColumns,
        [key]: !this.isColumnVisible(key),
      };
      this.saveVisibleColumns();
    },

    resetVisibleColumns() {
      this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      this.saveVisibleColumns();
    },

    closeColumnsMenu() {
      this.showColumnsMenu = false;
    },

    handleColumnsMenuOutsideClick(event) {
      if (!this.showColumnsMenu) {
        return;
      }
      const dropdown = this.$refs.columnsDropdown;
      if (dropdown && !dropdown.contains(event.target)) {
        this.closeColumnsMenu();
      }
    },

    getVisibleExportColumns() {
      return this.toggleableColumns.filter(col => this.isColumnVisible(col.key));
    },

    buildExportRow(history) {
      const hierarchy = `${history.originRegionName || ""} → ${history.destinationRegionName || ""}`;
      const lotteryInfo = this.getLotteryDisplayName(history);
      const statusLabel = this.getStatusLabel(history.status, history.betWin);
      const valorPremiado =
        history.betWin && history.awardValue > 0
          ? this.setCurrencyString(history.sobra)
          : "-";

      const values = {
        hierarchy,
        lottery: lotteryInfo,
        modality: history.modalityName || "",
        number: history.number || "",
        place: history.place || "",
        betValue: history.betValue || 0,
        currentPrice: history.currentPrice ? history.currentPrice.toFixed(0) : "-",
        valueAward: history.valueAward || 0,
        limit: history.limit || 0,
        sobra: history.sobra || 0,
        bancaPay: history.bancaPay ? history.bancaPay.toFixed(0) : "-",
        value: history.value || 0,
        status: statusLabel,
        awardValue: valorPremiado,
      };

      return this.getVisibleExportColumns().map(col => values[col.key]);
    },

    buildExportRowFormatted(history) {
      const hierarchy = `${history.originRegionName || ""} → ${history.destinationRegionName || ""}`;
      const lotteryInfo = this.getLotteryDisplayName(history);
      const statusLabel = this.getStatusLabel(history.status, history.betWin);
      const valorPremiado =
        history.betWin && history.awardValue > 0
          ? this.setCurrencyString(history.sobra)
          : "-";

      const values = {
        hierarchy,
        lottery: lotteryInfo,
        modality: history.modalityName || "",
        number: history.number || "",
        place: history.place || "",
        betValue: this.setCurrencyString(history.betValue || 0),
        currentPrice: history.currentPrice ? history.currentPrice.toFixed(0) : "-",
        valueAward: this.setCurrencyString(history.valueAward || 0),
        limit: this.setCurrencyString(history.limit),
        sobra: this.setCurrencyString(history.sobra),
        bancaPay: history.bancaPay ? history.bancaPay.toFixed(0) : "-",
        value: this.setCurrencyString(history.value || 0),
        status: statusLabel,
        awardValue: valorPremiado,
      };

      return this.getVisibleExportColumns().map(col => values[col.key]);
    },

    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
        } else {
          toast.error("Usuário não encontrado. Faça login novamente.");
          this.$router.push('/login');
        }
      } catch (error) {
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        if (this.isBanca) {
          // Para BANCA: buscar o BancaId do usuário e usar nova API para incluir regiões vinculadas
          const bancaResponse = await mainService.getUserBancaId(this.currentUser.id);
          this.userBancaId = bancaResponse.data;

          if (this.userBancaId) {
            // Usar nova API que busca regiões da banca incluindo vinculadas
            const bancaRegionsResponse = await mainService.getBancaRegionsWithLinked(this.userBancaId);
            this.userResponsibleRegions = bancaRegionsResponse.data.map(region => region.id);
          } else {
            this.userResponsibleRegions = [];
            toast.error("Usuário BANCA não possui BancaId definido!");
          }
        } else if (this.isAdmin) {
          // Para ADMIN: incluir todas as regiões disponíveis (filtradas por banca se selecionada)
          let allRegions = this.regions;
          
          // Se há banca selecionada, filtrar apenas regiões dessa banca
          if (this.currentSelectedBanca) {
            allRegions = allRegions.filter(region => 
              String(region.bancaId) === String(this.currentSelectedBanca.id)
            );
          }
          
          this.userResponsibleRegions = allRegions.map(region => region.id);
        } else {
          // Para GERENTE e COBRADOR: usar a API de regiões responsáveis incluindo vinculadas
          const response = await mainService.getUserRegionsWithLinked(this.currentUser.id);
          this.userResponsibleRegions = response.data.map(region => region.id);
          
          // Incluir a região principal também, se existir
          if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
            this.userResponsibleRegions.push(this.currentUser.regionId);
          }
        }
      } catch (error) {
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    async loadLotteries() {
      try {
        const lotteriesResponse = await mainService.searchLotteries();
        if (lotteriesResponse && lotteriesResponse.data) {
          const allLotteries = lotteriesResponse.data;
          
          // Filtrar apenas loterias ativas na região atual
          const currentRegion = JSON.parse(localStorage.getItem('currentRegion') || 'null');
          if (currentRegion && currentRegion.id) {
            try {
              const regionConfigsResponse = await mainService.searchRegionConfigs();
              if (regionConfigsResponse.data && regionConfigsResponse.data.regionLotteriesParameter) {
                const regionParams = regionConfigsResponse.data.regionLotteriesParameter;
                const activeLotteryDetailIds = new Set(
                  regionParams
                    .filter(param => param.isActive)
                    .map(param => param.lotteryDetailId)
                );
                
                // Filtrar loterias que têm pelo menos um horário ativo na região
                this.baseLotteries = [];
                allLotteries.forEach(lottery => {
                  if (lottery.details && Array.isArray(lottery.details)) {
                    const activeDetails = lottery.details.filter(detail => 
                      activeLotteryDetailIds.has(detail.id)
                    );
                    
                    if (activeDetails.length > 0) {
                      this.baseLotteries.push({
                        id: lottery.id,
                        name: lottery.name,
                        details: activeDetails.map(detail => ({
                          id: detail.id,
                          name: detail.description
                        }))
                      });
                    }
                  }
                });
              } else {
                // Se não houver configurações, usar todas as loterias
                this.baseLotteries = [];
                allLotteries.forEach(lottery => {
                  if (lottery.details && lottery.details.length > 0) {
                    this.baseLotteries.push({
                      id: lottery.id,
                      name: lottery.name,
                      details: lottery.details.map(detail => ({
                        id: detail.id,
                        name: detail.description
                      }))
                    });
                  }
                });
              }
            } catch (error) {
              // Se falhar ao buscar configurações, usar todas as loterias
              this.baseLotteries = [];
              allLotteries.forEach(lottery => {
                if (lottery.details && lottery.details.length > 0) {
                  this.baseLotteries.push({
                    id: lottery.id,
                    name: lottery.name,
                    details: lottery.details.map(detail => ({
                      id: detail.id,
                      name: detail.description
                    }))
                  });
                }
              });
            }
          } else {
            // Se não houver região selecionada, usar todas as loterias
            this.baseLotteries = [];
            allLotteries.forEach(lottery => {
              if (lottery.details && lottery.details.length > 0) {
                this.baseLotteries.push({
                  id: lottery.id,
                  name: lottery.name,
                  details: lottery.details.map(detail => ({
                    id: detail.id,
                    name: detail.description
                  }))
                });
              }
            });
          }
        } else {
          this.baseLotteries = [];
        }
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.baseLotteries = [];
      }
    },

    async searchRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data || [];
        
        // Carregar bancas se ainda não foram carregadas (para filtro por banca)
        if (!this.bancas || this.bancas.length === 0) {
          await this.loadBancas();
        }
        
        // Aguardar carregamento das regiões do usuário para todos os tipos
        await this.loadUserResponsibleRegions();
        
        // Filtrar apenas as regiões disponíveis para o usuário
        const regionsToSelect = this.availableRegions;
        
        if (regionsToSelect.length > 0) {
          if (this.isAdmin) {
            // ADMIN: verificar se há região selecionada
            const currentRegion = JSON.parse(localStorage.getItem('currentRegion') || 'null');
            if (currentRegion) {
              // Se há região selecionada, usar apenas ela
              this.selectedRegions = regionsToSelect.filter(r => r.id === currentRegion.id);
            } else {
              // Se não há região selecionada, usar todas
              this.selectedRegions = regionsToSelect;
            }
          } else {
            // Para outros roles, auto-selecionar todas as regiões disponíveis
            this.selectedRegions = regionsToSelect;
          }
        } else {
          this.loading = false;
          toast.warning("Nenhuma região disponível para consulta");
        }
      } catch (error) {
        this.loading = false;
        toast.error(handleErrorMessage(error));
        this.regions = [];
      }
    },

    async loadBancas() {
      const response = await mainService.searchBancas({ isActive: true });
      this.bancas = response.data || [];
    },

    updateSelectedRegions() {
      // Filtrar apenas as regiões disponíveis para o usuário
      const regionsToSelect = this.availableRegions;
      
      if (regionsToSelect.length > 0) {
        if (this.isAdmin) {
          // ADMIN: verificar se há região selecionada
          if (this.currentSelectedRegion) {
            // Se há região selecionada, usar apenas ela
            this.selectedRegions = regionsToSelect.filter(r => r.id === this.currentSelectedRegion.id);
          } else {
            // Se não há região selecionada, usar todas
            this.selectedRegions = regionsToSelect;
          }
        } else {
          // Para outros roles, auto-selecionar todas as regiões disponíveis
          this.selectedRegions = regionsToSelect;
        }
        
        // Limpar dados antigos
        this.predictions = [];
        this.searchExecuted = false;
      } else {
        this.selectedRegions = [];
        this.predictions = [];
        this.searchExecuted = false;
      }
    },

    initializeFilters() {
      // Definir data de hoje como padrão
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      this.startDate = todayStr;
      this.endDate = todayStr;
    },

    async searchPredictions() {
      if (this.selectedRegions.length === 0) {
        this.loading = false;
        toast.warning("Selecione pelo menos uma origem da descarga para buscar");
        return;
      }
      
      if (!this.startDate || !this.endDate) {
        this.loading = false;
        toast.warning("Informe as datas inicial e final");
        return;
      }

      // Validar se data início não é maior que data fim
      if (new Date(this.startDate) > new Date(this.endDate)) {
        this.loading = false;
        toast.warning("A data de início não pode ser maior que a data de fim");
        return;
      }

      this.loading = true;
      this.searchExecuted = true;
      this.currentPage = 1; // Resetar para primeira página
      
      try {
        // Determinar qual lotteryDetailId usar
        const lotteryDetailId = this.selectedLotteryTime || null;

        // IDs de horários da loteria selecionada (quando não há horário específico),
        // pré-calculados uma única vez para filtrar os resultados no frontend.
        const lotteryDetailIds = (this.selectedLottery && !this.selectedLotteryTime)
          ? this.availableTimes.map(t => t.id)
          : [];

        // Filtragem por loteria/horário aplicada ao retorno de cada região
        const applyLotteryFilter = (data) => {
          if (!Array.isArray(data)) {
            return [];
          }
          if (this.selectedLottery && !this.selectedLotteryTime) {
            return data.filter(h => lotteryDetailIds.includes(h.lotteryDetailId));
          }
          if (this.selectedLottery && this.selectedLotteryTime) {
            return data.filter(h => h.lotteryDetailId === this.selectedLotteryTime);
          }
          return data;
        };

        // PERFORMANCE: uma única requisição por região cobrindo TODO o intervalo de datas
        // (o backend agora aceita startDate/endDate), executadas em PARALELO com Promise.all.
        // Antes era um loop aninhado região × dia com awaits sequenciais (N × D requisições em fila).
        const requests = this.selectedRegions.map(region =>
          mainService.searchDescargaBetHistory(
            region.id,
            this.startDate,            // data inicial
            lotteryDetailId,           // horário específico (ou null = todos)
            this.selectedNumber || null,
            this.selectedStatus || null,
            this.selectedHierarchy || null,
            this.endDate               // data final do intervalo
          )
            .then(response => applyLotteryFilter(response.data))
            .catch(() => []) // uma região que falhe não derruba as demais
        );

        const results = await Promise.all(requests);
        const allHistory = results.flat();

        // Deduplicar registros baseado em chave única
        // A mesma descarga pode aparecer quando buscamos por região origem e destino
        // Isso acontece porque a API retorna a descarga tanto quando buscamos pela região origem quanto pela região destino
        const uniqueHistoryMap = new Map();
        allHistory.forEach(history => {
          // Criar chave única: incluir TODAS as propriedades que identificam unicamente um registro
          // Usar camelCase (padrão ASP.NET Core) com fallback para PascalCase
          const descargaBetId = history.descargaBetId || history.DescargaBetId || '';
          const originRegionId = history.originRegionId || history.OriginRegionId || '';
          const destinationRegionId = history.destinationRegionId || history.DestinationRegionId || '';
          const number = history.number || '';
          const place = history.place || 0;
          const lotteryDetailId = history.lotteryDetailId || history.LotteryDetailId || '';
          const currentPrice = history.currentPrice || history.CurrentPrice || 0;
          const hierarchyLevel = history.hierarchyLevel || history.HierarchyLevel || '';
          const betValue = history.betValue || history.BetValue || 0;
          
          // Chave única completa
          const uniqueKey = `${descargaBetId}_${originRegionId}_${destinationRegionId}_${number}_${place}_${lotteryDetailId}_${currentPrice}_${hierarchyLevel}_${betValue}`;
          
          if (!uniqueHistoryMap.has(uniqueKey)) {
            uniqueHistoryMap.set(uniqueKey, history);
          }
        });
        
        // Converter Map de volta para array
        const uniqueHistory = Array.from(uniqueHistoryMap.values());

        // Ordenar por data de criação (mais recente primeiro)
        uniqueHistory.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB - dateA; // Ordem decrescente
        });
        
        this.predictions = uniqueHistory;
        
        // DEBUG: Verificar os dados
        if (uniqueHistory.length > 0) {
          // Dados carregados com sucesso
          this.predictions = uniqueHistory;
        }
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.predictions = [];
      } finally {
        this.loading = false;
      }
    },

    setCurrencyString(value) {
      try {
        const numValue = Number(value) || 0;
        return jsFunctions.setCurrencyString(numValue);
      } catch (error) {
        return "R$ 0,00";
      }
    },

    validateNumberInput(event) {
      // Permitir apenas números e remover espaços
      const value = event.target.value;
      const numericValue = value.replace(/\D/g, '').trim();
      if (value !== numericValue) {
        this.selectedNumber = numericValue;
      }
    },

    getHierarchyClass(hierarchyLevel) {
      if (hierarchyLevel === 'Normal → Descarga') {
        return 'hierarchy-normal';
      } else if (hierarchyLevel === 'Descarga → Redescarga') {
        return 'hierarchy-redescarga';
      }
      return 'hierarchy-default';
    },

    getHierarchyTooltip(hierarchyLevel) {
      if (hierarchyLevel === 'Normal → Descarga') {
        return 'Normal';
      } else if (hierarchyLevel === 'Descarga → Redescarga') {
        return 'Redescarga';
      }
      return 'Descarga';
    },

    getStatusClass(status, betWin) {
      // Se o status for PROCESSED ou AWARDED, verificar se foi realmente premiado
      if ((status === 'PROCESSED' || status === 'AWARDED') && betWin === true) {
        return 'status-awarded'; // Verde - foi premiado
      } else if ((status === 'PROCESSED' || status === 'AWARDED') && betWin === false) {
        return 'status-not-awarded'; // Vermelho - não foi premiado
      }
      
      // Outros status
      switch (status) {
        case 'PENDING':
          return 'status-pending';
        case 'PROCESSED':
          return 'status-processed';
        case 'AWARDED':
          return 'status-awarded';
        default:
          return '';
      }
    },

    getStatusLabel(status, betWin) {
      // Se o status for PROCESSED ou AWARDED, verificar se foi realmente premiado
      if ((status === 'PROCESSED' || status === 'AWARDED') && betWin === true) {
        return 'Premiado';
      } else if ((status === 'PROCESSED' || status === 'AWARDED') && betWin === false) {
        return 'Não Premiado';
      }
      
      // Outros status
      switch (status) {
        case 'PENDING':
          return 'Pendente';
        case 'PROCESSED':
          return 'Processado';
        case 'AWARDED':
          return 'Premiado';
        default:
          return status;
      }
    },

    formatDateTime(dateTime) {
      try {
        const date = new Date(dateTime);
        return date.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (error) {
        return dateTime;
      }
    },
    
    getLotteryDisplayName(history) {
      // Tentar múltiplas fontes para o nome da loteria
      // 1. Se lotteryDetailName já vem do backend (ideal)
      if (history.lotteryDetailName) {
        return history.lotteryDetailName;
      }
      
      // 2. Se tem lotteryDetailId, buscar na lista de loterias carregadas
      if (history.lotteryDetailId && this.lotteries && this.lotteries.length > 0) {
        for (const lottery of this.lotteries) {
          if (lottery.details && Array.isArray(lottery.details)) {
            const detail = lottery.details.find(d => d.id === history.lotteryDetailId);
            if (detail) {
              // Retornar: NomeLoteria + Horário
              return `${lottery.name} ${detail.description}`;
            }
          }
        }
      }
      
      // 3. Fallback: tentar montar a partir de lotteryName + lotteryDetailDescription
      if (history.lotteryName && history.lotteryDetailDescription) {
        return `${history.lotteryName} ${history.lotteryDetailDescription}`;
      }
      
      // 4. Se só tem lotteryName
      if (history.lotteryName) {
        return history.lotteryName;
      }
      
      // 5. Último recurso
      return 'Não informado';
    },

    // Métodos do modal de detalhes das apostas
    openBetDetailsModal(history) {
      this.selectedBetDetails = history;
      this.showBetDetailsModal = true;
    },

    closeBetDetailsModal() {
      this.showBetDetailsModal = false;
      this.selectedBetDetails = null;
    },

    // Verificar se uma região é Redescarga (recebe descarga mas não envia)
    isRedescargaRegion(region) {
      // Uma região é Redescarga se:
      // 1. onlyDescarga = true
      // 2. Não tem descargaRegionId (não envia para ninguém) OU
      // 3. É destino de outra região de descarga
      if (!region.onlyDescarga) return false;
      
      // Verificar se alguma outra região envia descarga para esta
      const receivesFromDescarga = this.regions.some(r => 
        r.descargaRegionId && 
        String(r.descargaRegionId) === String(region.id) &&
        r.onlyDescarga // A região que envia também é de descarga
      );
      
      return receivesFromDescarga || !region.descargaRegionId;
    },

    printReport() {
      if (!this.filteredPredictions || this.filteredPredictions.length === 0) {
        toast.warning("Não há dados para imprimir");
        return;
      }

      const printWindow = window.open("", "_blank");
      const printHTML = this.generatePrintHTML();
      printWindow.document.write(printHTML);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    generatePrintHTML() {
      const predictions = this.filteredPredictions;
      const selectedRegionsText = this.selectedRegions.map(r => r.name || r.label).join(", ");
      const visibleColumns = this.getVisibleExportColumns();
      const headerRow = visibleColumns.map(col => `<th>${col.label}</th>`).join("");
      const tableRows = predictions.map(history => {
        const cells = this.buildExportRowFormatted(history)
          .map(value => `<td>${value}</td>`)
          .join("");
        return `<tr>${cells}</tr>`;
      }).join("");
      
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Relatório de Histórico de Descarga</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            h1 {
              text-align: center;
              color: #2c3e50;
              margin-bottom: 10px;
            }
            .report-info {
              text-align: center;
              margin-bottom: 20px;
              color: #666;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 11px;
            }
            th {
              background-color: #e5e5e5;
              padding: 10px;
              text-align: center;
              border: 1px solid #ddd;
              font-weight: bold;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 11px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Relatório de Histórico de Descarga</h1>
          <div class="report-info">
            <p><strong>Período:</strong> ${this.startDate} até ${this.endDate}</p>
            <p><strong>Regiões:</strong> ${selectedRegionsText}</p>
            ${this.selectedLottery ? `<p><strong>Loteria:</strong> ${this.baseLotteries.find(l => l.id === this.selectedLottery)?.name || ''}</p>` : ''}
            ${this.selectedNumber ? `<p><strong>Número:</strong> ${this.selectedNumber}</p>` : ''}
            ${this.selectedStatus ? `<p><strong>Status:</strong> ${this.getStatusLabel(this.selectedStatus)}</p>` : ''}
            <p><strong>Data de geração:</strong> ${new Date().toLocaleString("pt-BR")}</p>
          </div>
          <table>
            <thead>
              <tr>
                ${headerRow}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <div class="footer">
            <p>Total de registros: ${predictions.length}</p>
            <p>Total apostado: ${this.setCurrencyString(this.totalBetValue)}</p>
          </div>
        </body>
        </html>
      `;
    },

    exportToPDF() {
      if (!this.filteredPredictions || this.filteredPredictions.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const doc = new jsPDF("landscape", "mm", "a4");
        
        // Título
        doc.setFontSize(16);
        doc.text("Relatório de Histórico de Descarga", 14, 15);
        
        // Informações do relatório
        doc.setFontSize(10);
        doc.text(`Período: ${this.startDate} até ${this.endDate}`, 14, 25);
        const selectedRegionsText = this.selectedRegions.map(r => r.name || r.label).join(", ");
        const regionsText = doc.splitTextToSize(`Regiões: ${selectedRegionsText}`, 260);
        doc.text(regionsText, 14, 30);
        
        let yPos = 35;
        if (this.selectedLottery) {
          const lotteryName = this.baseLotteries.find(l => l.id === this.selectedLottery)?.name || '';
          doc.text(`Loteria: ${lotteryName}`, 14, yPos);
          yPos += 5;
        }
        if (this.selectedNumber) {
          doc.text(`Número: ${this.selectedNumber}`, 14, yPos);
          yPos += 5;
        }
        if (this.selectedStatus) {
          doc.text(`Status: ${this.getStatusLabel(this.selectedStatus)}`, 14, yPos);
          yPos += 5;
        }
        doc.text(`Data de geração: ${new Date().toLocaleString("pt-BR")}`, 14, yPos);
        
        const tableColumns = this.getVisibleExportColumns().map(col => col.label);
        const tableData = this.filteredPredictions.map(history =>
          this.buildExportRowFormatted(history),
        );
        
        // Adicionar tabela usando autoTable diretamente
        autoTable(doc, {
          head: [tableColumns],
          body: tableData,
          startY: yPos + 10,
          styles: { fontSize: 7, cellPadding: 1.5 },
          headStyles: { fillColor: [229, 229, 229], textColor: [0, 0, 0] },
          alternateRowStyles: { fillColor: [249, 249, 249] },
          margin: { left: 14, right: 14 },
          tableWidth: "wrap"
        });
        
        // Salvar PDF
        const fileName = `Historico_Descarga_${this.startDate}_${this.endDate}.pdf`;
        doc.save(fileName);
        toast.success("PDF exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar PDF:", error);
        toast.error("Erro ao exportar PDF");
      }
    },

    exportToExcel() {
      if (!this.filteredPredictions || this.filteredPredictions.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const headers = this.getVisibleExportColumns().map(col => col.label);
        const tableData = this.filteredPredictions.map(history =>
          this.buildExportRow(history),
        );
        
        // Criar workbook
        const wb = XLSX.utils.book_new();
        
        // Preparar dados completos: informações + cabeçalho + dados
        const allData = [
          ["Relatório de Histórico de Descarga"],
          [""],
          ["Período:", `${this.startDate} até ${this.endDate}`],
          ["Regiões:", this.selectedRegions.map(r => r.name || r.label).join(", ")],
        ];
        
        if (this.selectedLottery) {
          const lotteryName = this.baseLotteries.find(l => l.id === this.selectedLottery)?.name || '';
          allData.push(["Loteria:", lotteryName]);
        }
        if (this.selectedNumber) {
          allData.push(["Número:", this.selectedNumber]);
        }
        if (this.selectedStatus) {
          allData.push(["Status:", this.getStatusLabel(this.selectedStatus)]);
        }
        
        allData.push(
          ["Data de geração:", new Date().toLocaleString("pt-BR")],
          [""],
          ["Total de registros:", this.filteredPredictions.length],
          ["Total apostado:", this.totalBetValue],
          [""],
          headers, // Cabeçalho da tabela
          ...tableData // Dados da tabela
        );
        
        // Criar worksheet com todos os dados
        const ws = XLSX.utils.aoa_to_sheet(allData);
        
        // Definir largura das colunas
        const colWidths = headers.map(() => ({ wch: 15 }));
        ws["!cols"] = colWidths;
        
        // Adicionar worksheet ao workbook
        XLSX.utils.book_append_sheet(wb, ws, "Histórico Descarga");
        
        // Salvar arquivo
        const fileName = `Historico_Descarga_${this.startDate}_${this.endDate}.xlsx`;
        XLSX.writeFile(wb, fileName);
        toast.success("Excel exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar Excel:", error);
        toast.error("Erro ao exportar Excel");
      }
    }
  }
};
</script>

<style scoped>
/* Card header com ações — sobrescreve .btn-action global (32x32 ícone) */
.results-card {
  overflow: visible !important;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  z-index: 5;
  overflow: visible !important;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  z-index: 6;
}

.table-zoom-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary, var(--card-background));
}

.btn-zoom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-zoom:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.btn-zoom:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-label {
  min-width: 3rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.zoom-label:hover {
  color: var(--primary-color, #3b82f6);
}

.btn-action {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: auto !important;
  height: auto !important;
  min-width: unset !important;
  min-height: 36px;
  padding: 0.5rem 1rem !important;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  white-space: nowrap;
  box-sizing: border-box;
}

.btn-action i {
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
}

.btn-print {
  background: #6b7280;
}

.btn-print:hover {
  background: #4b5563;
}

.btn-pdf {
  background: #ef4444;
}

.btn-pdf:hover {
  background: #dc2626;
}

.btn-excel {
  background: #10b981;
}

.btn-excel:hover {
  background: #059669;
}

.columns-dropdown {
  position: relative;
  z-index: 50;
  flex: 0 0 auto;
}

.btn-columns {
  background-color: #2563eb;
}

.btn-columns:hover {
  background-color: #1d4ed8;
}

.columns-caret {
  font-size: 0.75rem;
  margin-left: 0.15rem;
}

.columns-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 1000;
  min-width: 220px;
  max-height: 360px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 0.5rem;
}

[data-theme="dark"] .columns-menu {
  background: #1f1f1f;
  border-color: #3f3f46;
}

.columns-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.5rem 0.6rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-primary, #111);
}

.columns-reset {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.columns-reset:hover {
  text-decoration: underline;
}

.columns-menu-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-primary, #111);
  user-select: none;
}

.columns-menu-item:hover {
  background: rgba(37, 99, 235, 0.08);
}

.columns-menu-item.locked {
  opacity: 0.7;
  cursor: default;
}

.columns-menu-item input {
  width: 15px;
  height: 15px;
  accent-color: #2563eb;
}

/* IMPORTANTE: Garantir que os dropdowns dos MultiSelect sobreponham todos os elementos */
/* Remover overflow: hidden dos containers para permitir que o dropdown apareça */
.entries-container .filters-card {
  overflow: visible !important;
}

.filters-content {
  overflow: visible !important;
}

.filters-grid {
  overflow: visible !important;
}

.input-group {
  overflow: visible !important;
  position: relative !important;
  z-index: 1 !important;
}

.multiselect-container {
  position: relative !important;
  z-index: 10 !important;
}

/* Quando o multiselect estiver ativo/aberto, aumentar o z-index */
.multiselect-container:has(.multiselect--active) {
  z-index: 1000 !important;
}

/* Table container com scroll horizontal */
.table-container {
  overflow-x: auto;
  max-width: 100%;
}

/* Tabela moderna com texto sem quebra de linha */
.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  white-space: nowrap;
}

.modern-table th {
  background: #e5e5e5;
  color: var(--text-primary);
  padding: 1rem 1.5rem;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

[data-theme="dark"] .modern-table th {
  background: #2d2d2d;
  color: #f4f4f5;
}

.modern-table td {
  padding: 0.75rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  white-space: nowrap;
}

.data-row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

[data-theme="dark"] .data-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.data-row:hover {
  background: rgba(96, 165, 250, 0.1);
}

/* Valores monetários */
.currency-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: var(--text-primary);
}

.currency-positive {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: #10b981 !important;
}

.currency-negative {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: #ef4444 !important;
}

/* Células específicas para números e colocação */
.number-cell {
  text-align: center;
  font-weight: 600;
  font-family: 'Courier New', monospace;
}

.place-cell {
  text-align: center;
  font-weight: 500;
}

/* Resumo consolidado específico */
.summary-content {
  padding: 1.5rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.summary-item {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  transition: all 0.3s ease;
}

.summary-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-family: 'Consolas', 'Monaco', monospace;
}

.summary-label {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Estilos específicos para filtros */
.form-input:disabled {
  background-color: var(--input-disabled-bg, #f5f5f5);
  color: var(--input-disabled-color, #888);
  cursor: not-allowed;
  opacity: 0.7;
}

[data-theme="dark"] .form-input:disabled {
  background-color: var(--input-disabled-bg, #2a2a2a);
  color: var(--input-disabled-color, #666);
}

/* Melhoria para o container de multiselect */
.multiselect-container {
  width: 100%;
}

/* Célula de loteria com nome e horário */
.lottery-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.lottery-name {
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
}

/* Badges de hierarquia */
.hierarchy-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: help;
  display: inline-block;
  position: relative;
}

.hierarchy-normal {
  background: #3b82f6;
  color: white;
}

.hierarchy-redescarga {
  background: #8b5cf6;
  color: white;
}

.hierarchy-default {
  background: #6b7280;
  color: white;
}

/* Badges de status */
.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-pending {
  background: #f59e0b;
  color: white;
}

.status-processed {
  background: #10b981;
  color: white;
}

.status-awarded {
  background: #10b981;
  color: white;
}

.status-not-awarded {
  background: #ef4444;
  color: white;
}

/* Células de região */
.region-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.region-cell i {
  color: #6b7280;
  font-size: 0.875rem;
}

[data-theme="dark"] .region-cell i {
  color: #9ca3af;
}

/* Responsividade para telas pequenas */
@media (max-width: 1024px) {
  .summary-grid {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }
}

@media (max-width: 768px) {
  .modern-table {
    font-size: 0.8rem;
  }
  
  .modern-table th,
  .modern-table td {
    padding: 0.5rem 0.75rem;
  }
  
  .summary-grid {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  }
}

@media (max-width: 480px) {
  .modern-table th,
  .modern-table td {
    padding: 0.375rem 0.5rem;
  }
  
  .summary-grid {
    grid-template-columns: 1fr;
  }
}

/* Paginação */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  flex-wrap: wrap;
  gap: 1rem;
}

.pagination-info {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  background: var(--card-background);
  color: var(--text-primary);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-page-info {
  padding: 0 1rem;
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .pagination-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .pagination-controls {
    justify-content: center;
  }
}

/* Valor clicável */
.clickable-value {
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  padding: 4px 8px;
  border-radius: 4px;
}

.clickable-value:hover {
  background: var(--primary-color);
  color: white;
  transform: translateY(-1px);
}

.clickable-value:active {
  transform: translateY(0);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 1rem;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-container {
  background: var(--card-background);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
}

.modal-title {
  color: white;
  margin: 0;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.modal-content {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.bet-details-info {
  background: var(--background-color);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: var(--text-secondary);
  font-weight: 500;
}

.info-value {
  color: var(--text-primary);
  font-weight: 600;
}

.bet-details-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.summary-card {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  color: white;
}

.summary-label {
  font-size: 0.875rem;
  opacity: 0.9;
  margin-bottom: 0.5rem;
}

.summary-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.bet-details-explanation {
  margin-top: 1.5rem;
}

.explanation-section {
  background: var(--background-color);
  border-left: 4px solid var(--primary-color);
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
}

.explanation-section h4 {
  color: var(--primary-color);
  margin-top: 0;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.explanation-section p {
  color: var(--text-primary);
  line-height: 1.6;
  margin: 0.5rem 0;
}

.explanation-section ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.explanation-section li {
  color: var(--text-primary);
  line-height: 1.8;
  margin: 0.25rem 0;
}

.example-box {
  background: rgba(var(--primary-color-rgb), 0.05);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 1rem;
  margin-top: 0.5rem;
}

.example-box p {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.warning-section {
  border-left-color: #f59e0b;
}

.warning-section h4 {
  color: #f59e0b;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  background: var(--background-color);
}

.btn-secondary {
  background: var(--secondary-color);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: var(--secondary-color-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.btn-secondary:active {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .modal-container {
    max-width: 100%;
    max-height: 95vh;
    margin: 0.5rem;
  }
  
  .modal-content {
    padding: 1rem;
  }
  
  .bet-details-summary {
    grid-template-columns: 1fr;
  }
}
</style> 